// apps/pwa-pos/src/lib/db.ts
// Sprint 99 POS.5 — persistencia offline del POS sobre IndexedDB.
//
// Reemplaza a localStorage para las colas, el historial y el catálogo: los
// ~5 MB compartidos de localStorage se agotaban y el fallo de escritura
// aparecía justo cuando más caro es, con una venta ya cobrada.
//
// Lo chico y síncrono (token de auth, print-setting, org-config, locale,
// sesión de caja, clientes) sigue en localStorage a propósito: su acceso
// síncrono durante el arranque mantiene simple el bootstrap.

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type {
  CatalogoSnapshot,
  PendingAbono,
  PendingAnular,
  PendingCajaOp,
  PendingVenta,
  VentaHistorialItem,
} from './queue-types';

export const DB_NAME = 'mibodega-pos';
export const DB_VERSION = 2;

export interface PosDBSchema extends DBSchema {
  ventas_queue: {
    key: string;
    value: PendingVenta;
    indexes: { estado: string; proximoIntentoAt: string };
  };
  caja_queue: {
    key: string;
    value: PendingCajaOp;
    indexes: { estado: string };
  };
  anular_queue: {
    key: string;
    value: PendingAnular;
    indexes: { estado: string };
  };
  abonos_queue: {
    key: string;
    value: PendingAbono;
    indexes: { estado: string };
  };
  historial: {
    key: string;
    value: VentaHistorialItem;
    indexes: { fecha: string };
  };
  catalogo: {
    key: [string, string];
    value: CatalogoSnapshot;
  };
}

let dbPromise: Promise<IDBPDatabase<PosDBSchema>> | null = null;
let dbHandle: IDBPDatabase<PosDBSchema> | null = null;

// `vi.resetModules()` re-crea este módulo entre tests; cada instancia mantiene
// su propio `dbHandle` y abriría su propia conexión sin saber de las demás.
// `deleteDB` no avanza hasta cerrar TODAS, así que el set vive en globalThis.
const OPEN_HANDLES_KEY = '__mibodega_pos_idb_handles__';
const PAGEHIDE_INSTALLED_KEY = '__mibodega_pos_idb_pagehide_installed__';
type HandlesGlobal = typeof globalThis & {
  [OPEN_HANDLES_KEY]?: Set<IDBPDatabase<PosDBSchema>>;
  [PAGEHIDE_INSTALLED_KEY]?: boolean;
};
const handlesGlobal = globalThis as HandlesGlobal;
if (!handlesGlobal[OPEN_HANDLES_KEY]) {
  handlesGlobal[OPEN_HANDLES_KEY] = new Set();
}
const allHandles: Set<IDBPDatabase<PosDBSchema>> = handlesGlobal[OPEN_HANDLES_KEY] as Set<
  IDBPDatabase<PosDBSchema>
>;
if (typeof window !== 'undefined' && !handlesGlobal[PAGEHIDE_INSTALLED_KEY]) {
  handlesGlobal[PAGEHIDE_INSTALLED_KEY] = true;
  const cerrarHandles = () => {
    for (const h of [...allHandles]) {
      allHandles.delete(h);
      h.close();
    }
  };
  window.addEventListener('pagehide', cerrarHandles);
  window.addEventListener('beforeunload', cerrarHandles);
}

export function getDb(): Promise<IDBPDatabase<PosDBSchema>> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(
      new Error('Este dispositivo no soporta IndexedDB. El POS no puede operar offline sin él.'),
    );
  }
  if (!dbPromise) {
    dbPromise = openDB<PosDBSchema>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          const ventas = db.createObjectStore('ventas_queue', { keyPath: 'id' });
          ventas.createIndex('estado', 'estado');
          // Ojo: IndexedDB omite del índice los registros cuya clave es null, y
          // `proximoIntentoAt` es null en las pendientes. El índice sirve para
          // inspeccionar, no para seleccionar qué reintentar: esa decisión la
          // toma `esReintentable` sobre la cola ya cargada.
          ventas.createIndex('proximoIntentoAt', 'proximoIntentoAt');

          db.createObjectStore('caja_queue', { keyPath: 'id' }).createIndex('estado', 'estado');
          db.createObjectStore('anular_queue', { keyPath: 'id' }).createIndex('estado', 'estado');
          db.createObjectStore('historial', { keyPath: 'id' }).createIndex('fecha', 'fecha');
          db.createObjectStore('catalogo', { keyPath: ['orgKey', 'sucursalKey'] });
        }
        if (oldVersion < 2 && !db.objectStoreNames.contains('abonos_queue')) {
          db.createObjectStore('abonos_queue', { keyPath: 'id' }).createIndex('estado', 'estado');
        }
      },
    }).then((db) => {
      dbHandle = db;
      allHandles.add(db);
      return db;
    });
  }
  return dbPromise;
}

/** IndexedDB no admite `null` en una clave: se normaliza igual que el cache viejo. */
export function catalogoKey(orgId: string | null, sucursalId: string | null): [string, string] {
  return [orgId ?? '_', sucursalId ?? '_'];
}

/** Sólo para tests: cierra TODAS las conexiones (incluso las de instancias
 *  anteriores creadas por `vi.resetModules`) y olvida la propia. */
export function resetDbForTests(): void {
  if (dbHandle) {
    allHandles.delete(dbHandle);
    dbHandle.close();
    dbHandle = null;
  }
  dbPromise = null;
  for (const h of [...allHandles]) {
    h.close();
    allHandles.delete(h);
  }
}
